import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import usePortalOptionValue, { usePortalOptionValueForm } from '@hooks/usePortalOptionValue'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { PortalOptionValueModel } from '@models/types'

const MODAL_KEY = 'CreateOrEditPortalOptionValueModal'

type ModalCallbacks = {
  closeModal: () => void,
  createPortalOptionValue: (payload: HandleCallbackActionParams) => Promise<object>,
  updatePortalOptionValue: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditPortalOptionValueModalProps = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<PortalOptionValueModel>,
  updateFn: Function,
}

const createOrEditPortalOptionValue = (params: CreateOrEditPortalOptionValueModalProps) => {
  const {
    callbacks, createFn, entityState, updateFn,
  } = params

  const { closeModal, createPortalOptionValue, updatePortalOptionValue } = callbacks || {}

  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const portalOptionValuePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Option Value ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updatePortalOptionValue(portalOptionValuePayload)

  // @ts-expect-error TS2345
  return createPortalOptionValue(portalOptionValuePayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditPortalOptionValueModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { portalOptionValue } = modalPayload

  const {
    callbacks: {
      createPortalOptionValue: createFn,
      deletePortalOptionValue: deleteFn,
      updatePortalOptionValue: updateFn,
    },
    creating,
    updating,
  } = usePortalOptionValue(portalOptionValue)

  const {
    entityState,
    entityState: {
      id,
      node_name,
      subtitle,
      title,
      value,
    },
    errors,
    handlers,
    saveEnabled,
  } = usePortalOptionValueForm(portalOptionValue, { validateOn: 'change' })

  const updateOrCreateButtonText = id ? 'Save Option Value' : 'Create Option Value'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Option Value" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Node Name" errorMessage={errors.node_name}>
            <input name="node_name" type="text" value={node_name} {...handlers} />
          </Form.Field>

          <Form.Field label="* Value" errorMessage={errors.value} marginTop="large">
            <input name="value" type="text" value={value} {...handlers} />
          </Form.Field>

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Subtitle" errorMessage={errors.subtitle} marginTop="large">
            <input name="subtitle" type="text" value={subtitle} {...handlers} />
          </Form.Field>

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryEdit"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                onClick={() => confirmDelete({
                  callbacks: { afterAction: closeModal, deleteFn },
                  strings: { entityName: 'Option Value' },
                })}
                size="medium"
              >
                Delete Option Value
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrEditPortalOptionValue({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPortalOptionValueModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
